import { CloseIcon, SearchIcon } from "@src/assets";
import { Navigation, PageLayout, TextInput } from "@src/components";
import { useSwapContext } from "@src/contexts";
import {
  forwardRef,
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChainItem } from "./ChainItem";
import { Virtuoso } from "react-virtuoso";
import { useLocation, useNavigate } from "react-router-dom";
import { includesIgnoreCase } from "@src/utils";
import { Chain } from "@src/models";
import { RoutePath } from "@src/interfaces";

export const ChainPickerView = () => {
  const [searchValue, setSearchValue] = useState("");
  const tokenSearchRef = useRef<HTMLInputElement | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { shouldUseTransferToken } = location.state || {};
  const {
    chain: selectedChain,
    token: selectedToken,
    setChain: setSelectedChain,
    setToken: setSelectedtoken,
    supportedChainsMap,
    transferToken,
    setTransferToken,
    chainToTokenOptionsMap,
  } = useSwapContext();

  useEffect(() => {
    tokenSearchRef.current?.focus();
  }, []);

  const chains = useMemo(() => {
    return Object.values(supportedChainsMap);
  }, [supportedChainsMap]);

  const filteredChains = useMemo(() => {
    return chains.filter(
      (chain) =>
        includesIgnoreCase(chain.chainId, searchValue) ||
        includesIgnoreCase(chain.name, searchValue),
    );
  }, [searchValue, chains]);

  const handleSwitchChain = useCallback(
    (chain: Chain) => {
      setSelectedChain(chain);

      const defaultToken = chainToTokenOptionsMap[chain.chainId]?.find(
        (token) => token.tokenId !== "USDC",
      );

      const newToken = chainToTokenOptionsMap[chain.chainId]?.find(
        (token) => token.tokenId === selectedToken?.tokenId,
      );
      setSelectedtoken(newToken || defaultToken);

      if (shouldUseTransferToken) {
        const newTransferToken = chainToTokenOptionsMap[chain.chainId]?.find(
          (token) => token.tokenId === transferToken?.tokenId,
        );

        setTransferToken(newTransferToken || defaultToken);
      }

      navigate(-1);
    },
    [
      chainToTokenOptionsMap,
      selectedToken,
      setSelectedChain,
      setSelectedtoken,
      shouldUseTransferToken,
      transferToken,
      setTransferToken,
      navigate,
    ],
  );

  const renderChainItem = useCallback(
    (_, chain) => {
      return (
        chain && (
          <ChainItem
            chain={chain}
            isSelected={chain.chainId === selectedChain?.chainId}
            onClick={() => {
              handleSwitchChain(chain);
            }}
          />
        )
      );
    },
    [handleSwitchChain, selectedChain],
  );

  return (
    <PageLayout
      navigation={
        <Navigation
          leftItems={[
            {
              content: "Pick network",
              disabled: true,
              path: RoutePath.PickChain,
            },
          ]}
          rightItems={[
            {
              content: <CloseIcon />,
              onClick: () => navigate(-1),
            },
          ]}
        />
      }
    >
      <div className="flex flex-col gap-4 h-full">
        <div className="bg-card_bg rounded-xl px-4">
          <TextInput
            ref={tokenSearchRef}
            className="h-10"
            inputIcon={
              <div className="fill-text_primary">
                <SearchIcon />
              </div>
            }
            placeholder="Search network"
            value={searchValue}
            handleChange={setSearchValue}
          />
        </div>
        <div className="flex flex-col mx-0 h-full">
          <div className="flex flex-col h-full">
            <Virtuoso
              className="h-full"
              data={filteredChains}
              itemContent={renderChainItem}
              components={{
                List: forwardRef<
                  HTMLDivElement,
                  HTMLAttributes<HTMLDivElement>
                >(({ style, ...props }, ref) => (
                  <div
                    ref={ref}
                    style={{
                      ...style,
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                    {...props}
                  />
                )),
              }}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
